import type { AuthUser } from './auth-provider.js';
import { USER_KEY } from './client.js';
import { resolveUserCrmMode } from './crm-mode-preference.js';
import {
  AUD_CURRENCY_ID,
  CNY_CURRENCY_ID,
  EUR_CURRENCY_ID,
  GBP_CURRENCY_ID,
  INR_CURRENCY_ID,
  JPY_CURRENCY_ID,
  KRW_CURRENCY_ID,
  SGD_CURRENCY_ID,
  USD_CURRENCY_ID,
} from './use-crm-mode.js';

export interface CurrencyConfig {
  /** ISO 4217 货币代码 */
  code: 'CNY' | 'USD' | 'EUR' | 'AUD' | 'GBP' | 'JPY' | 'INR' | 'KRW' | 'SGD';
  /** 货币符号 */
  symbol: '¥' | '$' | '€' | 'A$' | '£' | '₹' | '₩' | 'S$';
  /** Intl.NumberFormat locale (中文/英文模式 zh-CN / en-US) */
  locale: string;
  /** 是否海外用户 */
  isOverseas: boolean;
}

/**
 * 读取当前登录用户的默认货币配置 (D365 业务规则:
 *   - 归属"海外市场部"BU (或其后代) → USD ($)
 *   - 其他 BU → CNY (¥)
 *
 * 数据源: 后端 /me endpoint 返回的 isOverseas / defaultCurrency / defaultCurrencySymbol,
 *         缓存在 localStorage USER_KEY.
 * Fallback: 没有用户上下文 (未登录/旧浏览器缓存) → 默认 CNY.
 */
export function getCurrencyConfig(): CurrencyConfig {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (raw) {
      const me = JSON.parse(raw) as AuthUser;
      if (resolveUserCrmMode(me) === 'overseas') {
        return { code: 'USD', symbol: '$', locale: 'en-US', isOverseas: true };
      }
    }
  } catch {
    // ignore parse errors → fallback
  }
  return { code: 'CNY', symbol: '¥', locale: 'zh-CN', isOverseas: false };
}

/**
 * 根据货币 ID 获取货币配置.
 * 用于表单中用户手动选择货币时, 动态显示对应符号.
 *
 * @param currencyId - transactioncurrencyid 字段值
 * @returns 货币配置, 未选时默认人民币
 */
export function getCurrencyConfigById(currencyId: string | undefined): CurrencyConfig {
  // 映射所有 9 种支持的货币
  switch (currencyId) {
    case USD_CURRENCY_ID:
      return { code: 'USD', symbol: '$', locale: 'en-US', isOverseas: true };
    case EUR_CURRENCY_ID:
      return { code: 'EUR', symbol: '€', locale: 'en-US', isOverseas: true };
    case AUD_CURRENCY_ID:
      return { code: 'AUD', symbol: 'A$', locale: 'en-AU', isOverseas: true };
    case GBP_CURRENCY_ID:
      return { code: 'GBP', symbol: '£', locale: 'en-GB', isOverseas: true };
    case JPY_CURRENCY_ID:
      return { code: 'JPY', symbol: '¥', locale: 'ja-JP', isOverseas: true };
    case INR_CURRENCY_ID:
      return { code: 'INR', symbol: '₹', locale: 'en-IN', isOverseas: true };
    case KRW_CURRENCY_ID:
      return { code: 'KRW', symbol: '₩', locale: 'ko-KR', isOverseas: true };
    case SGD_CURRENCY_ID:
      return { code: 'SGD', symbol: 'S$', locale: 'en-SG', isOverseas: true };
    case CNY_CURRENCY_ID:
      return { code: 'CNY', symbol: '¥', locale: 'zh-CN', isOverseas: false };
    default:
      // 未选或未知货币，默认人民币
      return { code: 'CNY', symbol: '¥', locale: 'zh-CN', isOverseas: false };
  }
}

/**
 * 把数值格式化为当前用户默认货币的字符串.
 *
 * @example
 *   formatMoney(12345.6)
 *   // 国内用户: "¥ 12,345.60"
 *   // 海外用户: "$ 12,345.60"
 */
export function formatMoney(
  value: number | null | undefined,
  options: { decimals?: number } = {},
): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const cfg = getCurrencyConfig();
  const decimals = options.decimals ?? 2;
  return `${cfg.symbol} ${Number(value).toLocaleString(cfg.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/**
 * 把数值格式化为整数货币 (不显示小数, 用于列表 / badge 等场景).
 */
export function formatMoneyShort(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const cfg = getCurrencyConfig();
  return `${cfg.symbol} ${Number(value).toLocaleString(cfg.locale)}`;
}

// ── AntD InputNumber 通用 formatter / parser (M68: 动态货币符号) ─────

/**
 * AntD InputNumber formatter — 加千分位 + 货币符号前缀.
 *
 * @example
 *   <InputNumber formatter={moneyFormatter()} parser={moneyParser} />
 */
export function moneyFormatter(symbol?: string) {
  const s = symbol ?? getCurrencyConfig().symbol;
  return (val: string | number | undefined) =>
    val ? `${s} ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
}

/**
 * AntD InputNumber parser — 剥离货币符号 + 千分位逗号.
 * 支持所有货币符号（¥/$/ €/A$/£/₹/₩/S$），切换货币后旧输入也能正确解析.
 */
export const moneyParser = ((v?: string) =>
  v ? Number(v.replace(/[^\d.-]/g, '')) : 0) as unknown as (
  displayValue: string | undefined,
) => number;
